/**
 * districtGeoid.js
 *
 * Turns a candidate record into the key a voter's district lookup can be
 * joined against.
 *
 * WHY THIS EXISTS
 *
 * Candidates are stored with a human district label - "Senate District 1" -
 * and `useCandidates` filtered on it with exact string equality. Chamber
 * naming is not uniform across states, so the label the Census returns for a
 * voter rarely equals the label stored against a candidate. Observed live on
 * 2026-09-19: Virginia says "House District 78", New York "Assembly District
 * 75", Delaware "Representative District 32", Massachusetts "14th Suffolk
 * District". Outside Virginia-style states the query matched nothing, and
 * matching nothing renders identically to having no races.
 *
 * WHY THE KEY IS NOT JUST A GEOID
 *
 * The obvious fix is to store the Census GEOID. It is not sufficient on its
 * own. Census encodes an upper-chamber district as FIPS + three digits and a
 * lower-chamber district in exactly the same shape:
 *
 *   Roanoke      State Senate District 4   SLDU  51004
 *   Falls Church State House District 13   SLDL  51013
 *
 * Both readings are live values. Virginia has a Senate District 13 as well as
 * a House District 13, and both encode as "51013". A bare GEOID is therefore
 * ambiguous between chambers, and the failure it produces is the dangerous
 * kind: a voter silently shown the other chamber's candidates.
 *
 * So the stored key namespaces the GEOID by its Census layer - "sldu:51013"
 * against "sldl:51013". A caller who forgets the chamber matches nothing
 * rather than matching the wrong thing, which is the direction this codebase
 * fails in everywhere else.
 *
 * WHAT IT REFUSES TO DO
 *
 * Massachusetts ("14th Suffolk District") and New Hampshire ("House District
 * Merrimack 18") number their lower chambers on schemes a label cannot be
 * read back into. Reading "14" out of "14th Suffolk District" would yield
 * 25014 - a real Massachusetts district, and the wrong one. Every function
 * here returns null with a stated reason rather than a plausible value.
 */

/**
 * FIPS state codes. Two digits, leading zero significant - "06" is California
 * and "6" is nothing. Verified against live Census output for VA (51), MI
 * (26), NY (36) and DC (11).
 */
export const STATE_FIPS = Object.freeze({
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09',
  DE: '10', DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17',
  IN: '18', IA: '19', KS: '20', KY: '21', LA: '22', ME: '23', MD: '24',
  MA: '25', MI: '26', MN: '27', MS: '28', MO: '29', MT: '30', NE: '31',
  NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38',
  OH: '39', OK: '40', OR: '41', PA: '42', RI: '44', SC: '45', SD: '46',
  TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53', WV: '54',
  WI: '55', WY: '56', AS: '60', GU: '66', MP: '69', PR: '72', VI: '78',
});

/** Census layer identifiers, used as the key namespace. */
export const LAYERS = Object.freeze({ UPPER: 'sldu', LOWER: 'sldl', CONGRESS: 'cd' });

// An office elected by the whole state has no district. That is a fact about
// the office, not a data gap, and the backfill report must not chase it.
const STATEWIDE_OFFICE =
  /\b(u\.?\s?s\.?|united states)\s+(senator|senate)\b|\b(lieutenant\s+)?governor\b|\battorney\s+general\b|\bsecretary\s+of\s+state\b|\bpresident\b/i;

const CONGRESSIONAL_LABEL = /\bcongressional\b/i;
const CONGRESSIONAL_OFFICE =
  /\b(u\.?\s?s\.?|united states)\s+(representative|house|congress)|\bcongress(wo)?man\b|\bmember\s+of\s+congress\b/i;

const UPPER_LABEL = /\bsenate\b/i;
const UPPER_OFFICE = /\bstate\s+senator\b|\bsenator\b/i;

const LOWER_LABEL = /\bhouse\b|\bassembly\b|\brepresentative\b|\bdelegate/i;
const LOWER_OFFICE = /\bstate\s+(delegate|representative)\b|\bassembly\s*(member|man|woman)?\b|\bdelegate\b/i;

// The only label shape this module will read a number out of: some chamber
// wording, the word "District", then digits and nothing else. Everything the
// covered states produce either matches this exactly or is a scheme we cannot
// invert - there is deliberately no middle ground.
const NUMBERED_DISTRICT = /^.*\bdistrict\s+(\d{1,3})$/i;

/**
 * Reads the district number out of a label, or returns null.
 *
 * @param {string|null|undefined} label
 * @returns {number|null}
 */
export function parseDistrictNumber(label) {
  if (typeof label !== 'string') return null;
  const m = NUMBERED_DISTRICT.exec(label.trim());
  if (!m) return null;
  const n = Number(m[1]);
  // District 0 does not exist anywhere; a zero means the label was malformed.
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * Decides which Census layer a candidate record belongs to.
 *
 * Order matters. "U.S. Senator" is statewide and must not be read as a state
 * senate seat; "U.S. Representative" is congressional and must not be read as
 * a state lower-chamber seat. Both checks therefore run before the chamber
 * ones.
 *
 * @param {{district?: string|null, office?: string|null}} record
 * @returns {{layer: string|null, status: string, reason?: string}}
 */
export function classifyDistrict({ district, office } = {}) {
  const label = typeof district === 'string' ? district : '';
  const off = typeof office === 'string' ? office : '';

  if (off && STATEWIDE_OFFICE.test(off)) {
    return { layer: null, status: 'statewide', reason: `${off} is elected statewide and has no district` };
  }
  if (CONGRESSIONAL_LABEL.test(label) || (off && CONGRESSIONAL_OFFICE.test(off))) {
    return { layer: LAYERS.CONGRESS, status: 'classified' };
  }
  if (UPPER_LABEL.test(label) || (off && UPPER_OFFICE.test(off))) {
    return { layer: LAYERS.UPPER, status: 'classified' };
  }
  if (LOWER_LABEL.test(label) || (off && LOWER_OFFICE.test(off))) {
    return { layer: LAYERS.LOWER, status: 'classified' };
  }
  return {
    layer: null,
    status: 'unknown-chamber',
    reason: `Neither district ${JSON.stringify(district ?? null)} nor office ${JSON.stringify(office ?? null)} names a chamber`,
  };
}

/**
 * Builds the namespaced join key. Both parts are required - a key missing
 * either half would match something, and the something would be wrong.
 *
 * @param {string|null} layer one of LAYERS
 * @param {string|null} geoid
 * @returns {string|null}
 */
export function districtKey(layer, geoid) {
  if (!layer || !geoid) return null;
  return `${layer}:${geoid}`;
}

/**
 * Derives the join key for a stored candidate record.
 *
 * Never throws and never guesses. A null key always arrives with a `status`
 * saying which kind of null it is, because "this office has no district" and
 * "we could not read this label" need opposite responses from an operator.
 *
 * @param {{state?: string|null, district?: string|null, office?: string|null}} record
 * @returns {{key: string|null, layer: string|null, geoid: string|null, status: string, reason?: string}}
 */
export function deriveDistrictKey({ state, district, office } = {}) {
  const none = (status, reason) => ({ key: null, layer: null, geoid: null, status, reason });

  const chamber = classifyDistrict({ district, office });
  if (chamber.status === 'statewide') return none('statewide', chamber.reason);

  const code = typeof state === 'string' ? state.trim().toUpperCase() : '';
  const fips = STATE_FIPS[code];
  if (!fips) return none('unknown-state', `No FIPS code for state ${JSON.stringify(state ?? null)}`);

  if (chamber.status === 'unknown-chamber') return none('unknown-chamber', chamber.reason);

  const compact = typeof district === 'string' ? /^([A-Z]{2})-(\d{1,2})$/i.exec(district.trim()) : null;
  if (compact && (compact[1].toUpperCase() !== code || chamber.layer !== LAYERS.CONGRESS)) {
    return none('unparseable', 'Compact district label conflicts with state or congressional office');
  }
  const number = compact ? Number(compact[2]) : parseDistrictNumber(district);
  if (number === null) {
    return none(
      'unparseable',
      `District label ${JSON.stringify(district ?? null)} carries no district number this module can read without guessing`
    );
  }

  // Congressional districts are two digits wide, state legislative districts
  // three. Both are read straight off live Census GEOIDs: VA CD 4 is "5104",
  // VA Senate 14 is "51014".
  const width = chamber.layer === LAYERS.CONGRESS ? 2 : 3;
  if (String(number).length > width) {
    return none(
      'out-of-range',
      `District ${number} does not fit the ${width}-digit ${chamber.layer} field; the label is not the format we think it is`
    );
  }

  const geoid = `${fips}${String(number).padStart(width, '0')}`;
  return { key: districtKey(chamber.layer, geoid), layer: chamber.layer, geoid, status: 'derived' };
}

/**
 * Converts a districtLookup (#419) result into the same keys this module
 * stores against candidates.
 *
 * These GEOIDs come from the Census directly, so they are authoritative and
 * are deliberately NOT re-derived from their labels - re-deriving would let a
 * parsing bug overwrite a known-good value.
 *
 * @param {{stateSenate?: object|null, stateHouse?: object|null, congressional?: object|null}|null} districts
 * @returns {string[]}
 */
export function keysFromLookup(districts) {
  if (!districts || typeof districts !== 'object') return [];
  return [
    districtKey(LAYERS.UPPER, districts.stateSenate?.geoid ?? null),
    districtKey(LAYERS.LOWER, districts.stateHouse?.geoid ?? null),
    districtKey(LAYERS.CONGRESS, districts.congressional?.geoid ?? null),
  ].filter(Boolean);
}
